import type {
  CampaignCreationCapabilities,
  SmartCreationAutomaticBidInitialization,
} from "@adpilot/shared";
import { IconTargetOff, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { MoneyInput } from "@/components/patterns/MoneyInput";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ReviewQuickAddTargets } from "../ReviewQuickAddTargets";
import type { QuickTargetValue } from "../review-quick-targets";
import type { MatchFilterItem, QuickAddResult } from "./TargetCandidatesSection";
import {
  bidExpression,
  bidExpressionKey,
  type Entry,
  type GroupLane,
  validBid,
} from "./target-entries";
import type { ReviewBidPreview } from "./use-review-bid-preview";

interface SelectedTargetsSectionProps {
  negativeMode: boolean;
  groupKey: string;
  lane: GroupLane;
  defaultBid: number;
  capabilities?: CampaignCreationCapabilities;
  currencyCode: string;
  locked: boolean;
  entries: Entry[];
  visibleSelectedEntries: Entry[];
  selectedSearch: string;
  selectedMatchFilter: string;
  matchItems: MatchFilterItem[];
  bidPreview: ReviewBidPreview;
  automaticInitialization: SmartCreationAutomaticBidInitialization | undefined;
  entryLabel(entry: Entry): string;
  matchLabel(match: string): string;
  sourceLabel(entry: Entry): string;
  onAdd(values: QuickTargetValue[]): QuickAddResult;
  onEntriesChange(next: Entry[]): void;
  onSelectedSearchChange(search: string): void;
  onSelectedMatchFilterChange(filter: string): void;
  onRemoveEntry(entry: Entry): void;
}

export function SelectedTargetsSection({
  negativeMode,
  groupKey,
  lane,
  defaultBid,
  capabilities,
  currencyCode,
  locked,
  entries,
  visibleSelectedEntries,
  selectedSearch,
  selectedMatchFilter,
  matchItems,
  bidPreview,
  automaticInitialization,
  entryLabel,
  matchLabel,
  sourceLabel,
  onAdd,
  onEntriesChange,
  onSelectedSearchChange,
  onSelectedMatchFilterChange,
  onRemoveEntry,
}: SelectedTargetsSectionProps) {
  const { t, i18n } = useTranslation("smartCreation");
  const {
    bidPreviewQueries,
    bidPreviewLoading,
    bidPreviewFailed,
    bidByExpression,
    liveBidExpressionKeys,
  } = bidPreview;

  function recommendedBid(entry: Entry): number | null {
    const expression = bidExpression(entry);
    const suggestion = expression ? bidByExpression.get(bidExpressionKey(expression)) : undefined;
    if (!suggestion) return null;
    const decimals = capabilities?.bid.decimals ?? 2;
    const amount = Math.round(suggestion.suggestedBid * 10 ** decimals) / 10 ** decimals;
    return amount >= (capabilities?.bid.min ?? 0.01) &&
      amount <= (capabilities?.bid.max ?? 1_000_000)
      ? amount
      : null;
  }

  const moneyFormatter = new Intl.NumberFormat(i18n.resolvedLanguage, {
    style: "currency",
    currency: currencyCode,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: capabilities?.bid.decimals ?? 2,
  });
  const money = (value: number) => moneyFormatter.format(value);
  const currencySymbol =
    moneyFormatter.formatToParts(0).find((part) => part.type === "currency")?.value ?? currencyCode;

  function suggestionCell(entry: Entry) {
    const expression = bidExpression(entry);
    const suggestion = expression ? bidByExpression.get(bidExpressionKey(expression)) : undefined;
    if (!suggestion)
      return expression && bidPreviewLoading ? (
        <Skeleton className="h-4 w-16" />
      ) : (
        <span
          className="text-[11px] text-muted-foreground"
          title={t(
            expression
              ? "review.parity.suggestedBidUnavailable"
              : "review.parity.suggestedBidUnsupported",
          )}
        >
          {t(
            expression
              ? "review.targetTransfer.noSuggestion"
              : "review.targetTransfer.unsupportedSuggestion",
          )}
        </span>
      );
    const amount = recommendedBid(entry);
    const bidOutsideRange =
      Number(entry.bid) < suggestion.range.min || Number(entry.bid) > suggestion.range.max;
    return (
      <Tooltip>
        <TooltipTrigger render={<span className="flex flex-col items-start gap-0.5" />}>
          <Button
            variant="ghost"
            size="xs"
            className="h-4 px-0 text-xs font-medium text-primary"
            aria-label={t("review.targetTransfer.applyBid", {
              name: entryLabel(entry),
              bid: money(suggestion.suggestedBid),
            })}
            disabled={locked || amount === null}
            onClick={() => {
              if (amount !== null)
                onEntriesChange(
                  entries.map((item) =>
                    item.id === entry.id ? { ...item, bid: String(amount) } : item,
                  ),
                );
            }}
          >
            {money(suggestion.suggestedBid)}
          </Button>
          <span
            className={`whitespace-nowrap text-[10px] leading-3 tabular-nums ${bidOutsideRange ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}
          >
            {money(suggestion.range.min)}–{money(suggestion.range.max)}
          </span>
        </TooltipTrigger>
        <TooltipContent className="smart-creation-preview max-w-80">
          <span className="flex flex-col gap-1">
            {!liveBidExpressionKeys.has(bidExpressionKey(expression!)) &&
            automaticInitialization ? (
              <span>
                {t("review.targetTransfer.initialSnapshot", {
                  date: new Date(automaticInitialization.fetchedAt).toLocaleString(
                    i18n.resolvedLanguage,
                  ),
                })}
              </span>
            ) : null}
            <span>{t("review.parity.suggestedBidCalculation")}</span>
            {bidOutsideRange ? <span>{t("review.targetTransfer.outsideRange")}</span> : null}
            <span>{t("review.targetTransfer.applyHint")}</span>
          </span>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <section
      aria-label={t(negativeMode ? "review.parity.negativeTargets" : "review.parity.added")}
      className="flex min-h-0 min-w-0 flex-col gap-2"
    >
      <div className="flex h-8 shrink-0 items-center justify-between gap-2">
        <h3 className="text-xs font-semibold" aria-live="polite">
          {t(negativeMode ? "review.parity.negativeTargets" : "review.parity.added")}{" "}
          <span className="font-normal tabular-nums text-muted-foreground">{entries.length}</span>
        </h3>
        <div className="flex items-center gap-2">
          {negativeMode ? (
            <ReviewQuickAddTargets
              key={`${groupKey}:negative`}
              negative
              targetType={lane.type === "product" ? "product" : "keyword"}
              match="exact"
              defaultBid={defaultBid}
              bidLimits={capabilities?.bid}
              disabled={locked}
              onAdd={onAdd}
            />
          ) : (
            <Button
              variant="ghost"
              size="xs"
              disabled={locked || !entries.some((entry) => recommendedBid(entry) !== null)}
              onClick={() =>
                onEntriesChange(
                  entries.map((entry) => {
                    const amount = recommendedBid(entry);
                    return amount === null ? entry : { ...entry, bid: String(amount) };
                  }),
                )
              }
            >
              {t("review.targetTransfer.applyAllBids")}
            </Button>
          )}
          <Button
            variant="ghost"
            size="xs"
            disabled={locked || !entries.length}
            onClick={() => onEntriesChange([])}
          >
            {t("review.parity.removeAll")}
          </Button>
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <Input
          size="sm"
          className="min-w-0 flex-1"
          value={selectedSearch}
          aria-label={t("review.targetTransfer.searchSelected")}
          placeholder={t("review.targetTransfer.searchSelected")}
          onChange={(event) => onSelectedSearchChange(event.target.value)}
        />
        <Select
          value={selectedMatchFilter}
          items={matchItems}
          onValueChange={(value) => onSelectedMatchFilterChange(String(value ?? "all"))}
        >
          <SelectTrigger
            size="sm"
            className="w-32"
            aria-label={t("review.targetTransfer.filterSelectedMatch")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {matchItems.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <DataTableFrame
        className="flex min-h-0 flex-1 flex-col rounded-lg"
        viewportClassName="min-h-0 flex-1 overflow-auto"
      >
        <DataTable
          density="compact"
          stickyHeader
          className="w-full table-fixed text-xs [&_thead_th]:h-8 [&_thead_th]:px-2 [&_thead_th]:text-[11px] [&_thead_th]:normal-case [&_thead_th]:tracking-normal [&_tbody_td]:px-2 [&_tbody_td]:py-1.5 [&_tbody_tr]:h-12"
        >
          <TableHeader>
            <TableRow>
              <TableHead>{t("review.parity.target")}</TableHead>
              {!negativeMode ? (
                <>
                  <TableHead className="w-20">{t("review.parity.bid")}</TableHead>
                  <TableHead className="w-33">{t("review.parity.suggestedBid")}</TableHead>
                  <TableHead className="w-26">{t("review.parity.source")}</TableHead>
                </>
              ) : null}
              <TableHead className="w-9" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleSelectedEntries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="truncate" title={entryLabel(entry)}>
                  <p className="truncate leading-4">{entryLabel(entry)}</p>
                  <span className="mt-0.5 block text-[10px] leading-3 text-muted-foreground">
                    {matchLabel(entry.match)}
                  </span>
                </TableCell>
                {!negativeMode ? (
                  <>
                    <TableCell>
                      <MoneyInput
                        size="sm"
                        containerClassName="h-7 w-full [&_input]:h-full [&_input]:px-1.5 [&_input]:[appearance:textfield] [&_input::-webkit-inner-spin-button]:appearance-none [&_input::-webkit-outer-spin-button]:appearance-none"
                        className="min-w-0 text-xs tabular-nums"
                        type="number"
                        min={capabilities?.bid.min ?? 0.01}
                        max={capabilities?.bid.max ?? 1_000_000}
                        step={10 ** -(capabilities?.bid.decimals ?? 2)}
                        aria-label={t("review.parity.bidLabel", {
                          name: entryLabel(entry),
                        })}
                        value={entry.bid}
                        currencySymbol={currencySymbol}
                        aria-invalid={!validBid(entry.bid, capabilities?.bid)}
                        disabled={locked}
                        onChange={(event) =>
                          onEntriesChange(
                            entries.map((item) =>
                              item.id === entry.id ? { ...item, bid: event.target.value } : item,
                            ),
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>{suggestionCell(entry)}</TableCell>
                    <TableCell
                      className="truncate text-xs text-foreground/75"
                      title={sourceLabel(entry)}
                    >
                      {sourceLabel(entry)}
                    </TableCell>
                  </>
                ) : null}
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    disabled={locked}
                    aria-label={t("review.parity.removeTarget", {
                      name: entryLabel(entry),
                    })}
                    title={t("review.targetTransfer.returnToCandidates")}
                    onClick={() => onRemoveEntry(entry)}
                  >
                    <IconX />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </DataTable>
        {!visibleSelectedEntries.length ? (
          <Empty className="min-h-32 gap-2 p-4">
            <EmptyHeader className="gap-2">
              <EmptyMedia className="mb-0">
                <IconTargetOff className="size-5" />
              </EmptyMedia>
              <EmptyTitle className="text-xs">
                {t(
                  entries.length
                    ? "review.targetSelection.noResults"
                    : negativeMode
                      ? "review.sheet.noNegatives"
                      : "review.sheet.noTargets",
                )}
              </EmptyTitle>
              <EmptyDescription className="max-w-64 text-xs">
                {t(
                  negativeMode
                    ? "review.sheet.noNegativesDescription"
                    : "review.targetTransfer.emptySelected",
                )}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : null}
      </DataTableFrame>
      {!negativeMode &&
      (bidPreviewFailed ||
        (automaticInitialization && automaticInitialization.status !== "recommended")) ? (
        <div className="flex shrink-0 items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span>
            {t(
              automaticInitialization?.items.length
                ? "review.parity.bidRefreshFallback"
                : "review.parity.bidRecommendationFallback",
            )}
          </span>
          <Button
            variant="ghost"
            size="xs"
            disabled={locked || bidPreviewLoading || !bidPreviewQueries.length}
            onClick={() => {
              for (const query of bidPreviewQueries) void query.refetch();
            }}
          >
            {t("review.parity.retryBidRecommendation")}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
